import React from 'react';
import { TrendingUp } from 'lucide-react';
import { isWhiteLabel } from '../../config/tenantConfig';
import type { AnalyticsSummary } from '../../types/analytics';

interface Props {
  summary: AnalyticsSummary;
  title?: string;
  period?: string;
}

const fmtInr = (n: number) =>
  '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.abs(n));
const fmtInrSigned = (n: number) => (n >= 0 ? '+' : '-') + fmtInr(n);

const ACCENT = {
  blue: { iconCls: 'text-blue-600 dark:text-blue-400',  badge: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  zebu: { iconCls: 'text-zebu-600 dark:text-zebu-400',  badge: 'bg-zebu-50 text-zebu-700 dark:bg-zebu-900/30 dark:text-zebu-300' },
} as const;

const signedClass = (n: number | null) =>
  n === null || n === undefined ? 'text-gray-700 dark:text-gray-300'
    : n > 0 ? 'text-emerald-600 dark:text-emerald-400'
    : n < 0 ? 'text-red-600 dark:text-red-400'
    : 'text-gray-700 dark:text-gray-300';

const fmtPctSigned = (n: number | null) =>
  n === null || n === undefined ? '—'
    : `${n >= 0 ? '+' : ''}${n}%`;

const fmtPct = (n: number | null) =>
  n === null || n === undefined ? '—' : `${n}%`;

const fmtNum = (n: number | null, decimals = 2) =>
  n === null || n === undefined ? '—' : n.toFixed(decimals);

/**
 * Full-width KPI strip card for the Analytics Overview. Mirrors the inline
 * 8-stat grid inside `PerformanceReport` but adds a 9th cell — Capital
 * Deployed — and renders as a standalone card with the same header chrome.
 */
const KpiStripCard: React.FC<Props> = ({ summary, title = 'Live Performance', period }) => {
  const accent = ACCENT[isWhiteLabel() ? 'zebu' : 'blue'];

  const items: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'Total Return',  value: <span className={signedClass(summary.totalReturn)}>{fmtPctSigned(summary.totalReturn)}</span> },
    { label: 'CAGR',          value: <span className={signedClass(summary.cagr)}>{summary.cagr === null ? '—' : `${summary.cagr >= 0 ? '+' : ''}${summary.cagr}% / yr`}</span> },
    { label: 'Net P&L',       value: <span className={signedClass(summary.netPnl)}>{fmtInrSigned(summary.netPnl)}</span> },
    { label: 'Win Rate',      value: <span className="text-emerald-600 dark:text-emerald-400">{fmtPct(summary.winRate)}</span> },
    { label: 'Sharpe Ratio',  value: fmtNum(summary.sharpeRatio) },
    { label: 'Max Drawdown',  value: <span className="text-red-600 dark:text-red-400">-{fmtInr(summary.maxDrawdown)}</span> },
    { label: 'Profit Factor', value: fmtNum(summary.profitFactor) },
    { label: 'Total Trades',  value: summary.totalTrades },
    {
      label: 'Capital Deployed',
      value: summary.capitalDeployed === null || summary.capitalDeployed === 0
        ? <span className="text-gray-400 dark:text-gray-500">—</span>
        : <span className="text-gray-900 dark:text-white">{fmtInr(summary.capitalDeployed)}</span>,
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className={`w-5 h-5 ${accent.iconCls}`} />
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
        </div>
        {period && (
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${accent.badge}`}>{period}</span>
        )}
      </div>
      <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {items.map((item) => (
          <div key={item.label}>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{item.label}</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">{item.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KpiStripCard;
